import React from "react";
import Select, { StylesConfig } from "react-select";
import { useTranslation } from "react-i18next";
import { SpaceTemplateOption } from "app/routes/Scheduling/Pricing/useSpaceTemplates";

import css from "./spaceTemplateSelector.module.scss";

interface SpaceTemplateSelectorProps {
  templates: SpaceTemplateOption[];
  value?: string | null;
  onChange: (_templateId: string | null) => void;
  styles: StylesConfig;
}

const IndicatorSeparator = (): JSX.Element | null => null;

/**
 * Links a booking option to a space template (CN-2595). The template is applied
 * to the space auto-created for an athlete who is new to the coach when they
 * book this option.
 */
export default function SpaceTemplateSelector({
  templates,
  value,
  onChange,
  styles,
}: SpaceTemplateSelectorProps) {
  const { t } = useTranslation();

  const options = templates.map((template) => ({
    value: template.id,
    label: template.name,
  }));

  return (
    <div className={css.container}>
      <label htmlFor="spaceTemplateId">{t("Template")}</label>
      <Select
        inputId="spaceTemplateId"
        // Gives the control a stable class to size from: the emotion class
        // react-select generates on its own changes with the build.
        classNamePrefix="space-template-select"
        value={options.find((option) => option.value === value) || null}
        onChange={(selectedOption) =>
          onChange(
            (selectedOption as { value: string; label: string })?.value || null
          )
        }
        options={options}
        styles={{
          ...(styles as StylesConfig),
          menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
        }}
        components={{ IndicatorSeparator }}
        placeholder={t("No template")}
        isClearable
        // The menu floats over the page instead of growing the modal it sits
        // in: an inline menu near the bottom of a scrollable modal pushes the
        // modal past its own box and the scrollbar turns up outside it
        // (CFTB-3534). Portalled and fixed, so it is positioned against the
        // window and no container's scroll height changes; `auto` flips it
        // upwards when there is no room below.
        menuPortalTarget={
          typeof document !== "undefined" ? document.body : undefined
        }
        menuPosition="fixed"
        menuPlacement="auto"
        noOptionsMessage={() =>
          templates.length === 0
            ? t("You have no templates yet")
            : t("No templates match your search")
        }
      />
      {/* A <p>, not a <span>: the modal styles every span inside a field row
          as its red validation message. */}
      <p className={css.hint}>
        {t(
          "Applied to the space automatically created for an athlete who is new to you when they book this option."
        )}
      </p>
    </div>
  );
}
